/**
 * Eyes Follow Mouse Widget SDK
 * Embeddable widget that tracks mouse movement across the entire page
 */

import { AngleCalculator } from './angle-calculator';
import { ImageLoader } from './image-loader';
import type {
  ApiResponse,
  EmbeddedGazeTrackerOptions,
  GridData,
} from './types';

export class EmbeddedGazeTracker {
  private container: HTMLElement;
  private options: Required<
    Omit<EmbeddedGazeTrackerOptions, 'preloadedData'>
  > & {
    preloadedData?: GridData;
  };
  private imageLoader: ImageLoader;
  private angleCalculator: AngleCalculator;
  private gridData: GridData | null = null;
  private currentImageIndex = 40; // Default to center
  private currentImageElement: HTMLImageElement | null = null;
  private isInitialized = false;
  private loadingElement: HTMLElement | null = null;

  constructor(
    containerId: string,
    options: Omit<
      EmbeddedGazeTrackerOptions,
      'width' | 'height' | 'trackFullPage'
    > & {
      width?: number;
      height?: number;
      trackFullPage?: boolean;
      preloadedData?: GridData;
    }
  ) {
    // Get container element
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id "${containerId}" not found`);
    }
    this.container = container;

    // Set default options
    this.options = {
      generationId: options.generationId,
      token: options.token,
      width: options.width || 200,
      height: options.height || 200,
      trackFullPage: options.trackFullPage ?? true,
      preloadedData: options.preloadedData,
    };

    // Initialize helpers
    this.imageLoader = new ImageLoader();
    this.angleCalculator = new AngleCalculator(9); // 9x9 grid

    // Start initialization
    this.init();
  }

  /**
   * Initialize the widget
   */
  private async init(): Promise<void> {
    try {
      // 1. Show loading state
      this.showLoading();

      // 2. Use preloaded data or fetch from API
      if (this.options.preloadedData) {
        console.log('Using preloaded grid data (faster!)');
        this.gridData = this.options.preloadedData;
      } else {
        console.log('Fetching grid data from API...');
        this.gridData = await this.fetchGridData();
      }

      if (!this.gridData || !this.gridData.images) {
        throw new Error('Invalid grid data received');
      }

      // 3. Preload all images
      const imageUrls = this.gridData.images.map((img) => img.imageUrl);
      await this.imageLoader.preloadImages(imageUrls, (loaded, total) => {
        this.updateLoadingProgress(loaded, total);
      });

      // 4. Create image display element
      this.createImageElement();

      // 5. Start tracking mouse movement
      this.startTracking();

      // 6. Hide loading state
      this.hideLoading();

      this.isInitialized = true;
    } catch (error) {
      console.error('EmbeddedGazeTracker initialization failed:', error);
      this.showError(
        error instanceof Error ? error.message : 'Failed to load widget'
      );
    }
  }

  /**
   * Fetch grid data from API
   */
  private async fetchGridData(): Promise<GridData> {
    const apiUrl = this.getApiUrl();

    const response = await fetch(apiUrl);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid or missing embed token');
      }
      if (response.status === 404) {
        throw new Error('Generation not found');
      }
      throw new Error(`Failed to fetch data: ${response.statusText}`);
    }

    const data: ApiResponse = await response.json();

    if (!data.success || !data.gridData) {
      throw new Error(data.error || 'Failed to load grid data');
    }

    return data.gridData;
  }

  /**
   * Get API URL based on environment
   */
  private getApiUrl(): string {
    // Use current origin to support both dev and production
    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : 'https://rlu.ai';

    return `${baseUrl}/api/eyes-follow-mouse-widget/${this.options.generationId}?token=${this.options.token}`;
  }

  /**
   * Create the image display element
   */
  private createImageElement(): void {
    // Set container styles
    this.container.style.width = `${this.options.width}px`;
    this.container.style.height = `${this.options.height}px`;
    this.container.style.position = 'relative';
    this.container.style.overflow = 'hidden';
    this.container.style.borderRadius = '8px';

    // Get center image (default)
    const centerIndex = Math.floor(this.gridData!.images.length / 2);
    const centerImage = this.imageLoader.getImage(centerIndex);

    if (centerImage) {
      this.currentImageElement = this.createStyledImage(centerImage);
      this.container.appendChild(this.currentImageElement);
      this.currentImageIndex = centerIndex;
    }
  }

  /**
   * Create a styled image element
   */
  private createStyledImage(img: HTMLImageElement): HTMLImageElement {
    const styledImg = img.cloneNode(true) as HTMLImageElement;
    styledImg.style.width = '100%';
    styledImg.style.height = '100%';
    styledImg.style.objectFit = 'cover';
    styledImg.style.position = 'absolute';
    styledImg.style.top = '0';
    styledImg.style.left = '0';
    styledImg.style.transition = 'opacity 0.1s ease-out';
    return styledImg;
  }

  /**
   * Start tracking mouse movement
   */
  private startTracking(): void {
    const trackingTarget = this.options.trackFullPage
      ? document
      : this.container;

    trackingTarget.addEventListener(
      'mousemove',
      this.handleMouseMove as EventListener
    );
  }

  /**
   * Handle mouse move event
   */
  private handleMouseMove = (e: MouseEvent): void => {
    if (!this.isInitialized || !this.gridData) return;

    // Get container position
    const rect = this.container.getBoundingClientRect();

    // Calculate image index using ratio-based approach
    const imageIndex = this.angleCalculator.calculateImageIndexByRatio(
      e.clientX,
      e.clientY,
      rect,
      this.gridData.images
    );

    // Update displayed image if changed
    if (imageIndex !== this.currentImageIndex) {
      this.updateImage(imageIndex);
    }
  };

  /**
   * Update the displayed image
   */
  private updateImage(index: number): void {
    const newImage = this.imageLoader.getImage(index);
    if (!newImage || !this.currentImageElement) return;

    // Create new image element
    const newImageElement = this.createStyledImage(newImage);

    // Fade out current image
    this.currentImageElement.style.opacity = '0';

    // Add new image
    this.container.appendChild(newImageElement);

    // Remove old image after transition
    setTimeout(() => {
      if (this.currentImageElement && this.currentImageElement.parentNode) {
        this.currentImageElement.parentNode.removeChild(
          this.currentImageElement
        );
      }
      this.currentImageElement = newImageElement;
    }, 100);

    this.currentImageIndex = index;
  }

  /**
   * Show loading state
   */
  private showLoading(): void {
    this.loadingElement = document.createElement('div');
    this.loadingElement.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    this.loadingElement.innerHTML = `
      <div style="text-align: center;">
        <div style="
          width: 40px;
          height: 40px;
          border: 4px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        "></div>
        <p style="margin-top: 12px; font-size: 14px; opacity: 0.9;" id="loading-text">Loading...</p>
      </div>
      <style>
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;

    this.container.appendChild(this.loadingElement);
  }

  /**
   * Update loading progress
   */
  private updateLoadingProgress(loaded: number, total: number): void {
    const loadingText = this.loadingElement?.querySelector('#loading-text');
    if (loadingText) {
      const percentage = Math.round((loaded / total) * 100);
      loadingText.textContent = `Loading ${percentage}%`;
    }
  }

  /**
   * Hide loading state
   */
  private hideLoading(): void {
    if (this.loadingElement && this.loadingElement.parentNode) {
      this.loadingElement.parentNode.removeChild(this.loadingElement);
      this.loadingElement = null;
    }
  }

  /**
   * Show error state
   */
  private showError(message: string): void {
    this.container.innerHTML = `
      <div style="
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fee;
        color: #c00;
        padding: 20px;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        border-radius: 8px;
      ">
        <div>
          <strong>Error</strong><br/>
          ${message}
        </div>
      </div>
    `;
  }

  /**
   * Destroy the widget and clean up
   */
  public destroy(): void {
    const trackingTarget = this.options.trackFullPage
      ? document
      : this.container;
    trackingTarget.removeEventListener(
      'mousemove',
      this.handleMouseMove as EventListener
    );

    this.container.innerHTML = '';
    this.imageLoader.clear();
    this.isInitialized = false;
  }
}

// Export to global window object for use in browser
if (typeof window !== 'undefined') {
  (window as any).EmbeddedGazeTracker = EmbeddedGazeTracker;
}

export default EmbeddedGazeTracker;
